import { useImperativeHandle } from 'react';

import { type TabsHashMenuHandle, type TabsHashMenuProps } from './router.types';

import { typedForwardRef } from '@/shared/utils/react';

import { useTabsHashConfig, useTabsHashControl } from './router.hooks';
import { useTabsNavigationEvents } from '../menu/TabsMenu.hooks';

import TabsMenu from '../menu/TabsMenu';

const DISPLAY_NAME = 'TabsHashMenu';

const TabsHashMenu = typedForwardRef(
  <T extends string>(
    { allowInitialHiddenTab, defaultTab, onChange, tab, tabConfigs, ...rest }: TabsHashMenuProps<T>,
    ref: React.Ref<TabsHashMenuHandle<T>>,
  ) => {
    const config = useTabsHashConfig(tabConfigs);

    const hashControl = useTabsHashControl({
      allowInitialHiddenTab,
      config,
      defaultTab,
      onChange,
      tab,
    });

    const navigateByIndex = (index: number) => {
      let clampedIndex = index;

      if (index < config.bounds.min) clampedIndex = config.bounds.max;
      else if (index > config.bounds.max) clampedIndex = config.bounds.min;

      const hash = config.indexToValue(clampedIndex);

      if (hash !== null) hashControl.onChange(hash);
    };

    const currentTabIndex = hashControl.tab ? config.valueToIndex(hashControl.tab) : null;

    useTabsNavigationEvents({
      onDigit: (e, digit) => {
        const index = digit - 1;

        if (!config.list[index]) return;

        e.preventDefault();
        navigateByIndex(index);
      },
      onNext: () => {
        const nextIndex = currentTabIndex == null ? config.bounds.min : currentTabIndex + 1;

        navigateByIndex(nextIndex);
      },
      onPrev: () => {
        const nextIndex = currentTabIndex == null ? config.bounds.max : currentTabIndex - 1;

        navigateByIndex(nextIndex);
      },
    });

    useImperativeHandle(ref, () => ({
      goToTab: (query) => hashControl.onChange(query.hash),
    }));

    return (
      <TabsMenu.Base
        {...rest}
        activeTab={hashControl.tab}
        config={config}
        onChange={hashControl.onChange}
      />
    );
  },
  DISPLAY_NAME,
);

export default TabsHashMenu;
